// OpenAI (ChatGPT Ads) conversion pixel — the `oaiq` SDK.
//
// Unlike gtag.ts, the setup snippet is NOT injected from here — it lives in
// index.html's <head>. That's deliberate and follows OpenAI's own instruction
// to install it "near the top" of the head: the SDK reads an `oppref` click
// identifier off the landing URL, and this is a SPA that navigates away from
// the ad URL shortly after mount (see the same hazard documented in track.ts).
// The earlier the SDK request starts, the less chance that read races the
// first navigation.
//
// The snippet defines `window.oaiq` as a synchronous queue *before* the async
// SDK arrives, so every call in this file is safe to make at any time — early
// calls are buffered and replayed once the SDK loads. `window.oaiq` missing at
// all means the snippet was removed, and everything here becomes a no-op.
//
// The pixel ID is not a secret (it's in the page source of every site running
// this pixel, same as the Google Ads conversion ID) and lives in index.html
// beside the init call, so there's exactly one copy of it.
//
// CONSENT: like gtag.ts, this deliberately implements no consent gating. The
// site has no consent banner and Clarity/Meta/Google all run on the same
// footing today. **Revisit before targeting any EEA/UK traffic.**

/** Standard OpenAI event names we emit. All three take the `contents` data
 *  shape. The full taxonomy also has lead_created / registration_completed /
 *  subscription_created etc., which don't describe anything this site does —
 *  notably `checkout_email_entered` is NOT mapped to `lead_created`, because
 *  entering an email mid-checkout isn't a lead-form submission and mislabelling
 *  it would pollute optimisation if conversion bidding is turned on later. */
type ContentsEvent = 'order_created' | 'checkout_started' | 'items_added'

type OaiqFn = (...args: unknown[]) => void
declare global {
  interface Window {
    oaiq?: OaiqFn
  }
}

/** Low-level send. `amount` is an integer in ISO 4217 *minor* units (cents),
 *  which is exactly how prices are stored throughout this codebase, so values
 *  pass through unscaled. `currency` is required whenever `amount` is set. */
function measure(
  event: ContentsEvent,
  data?: { amountCents?: number | null; currency?: string | null },
  eventId?: string,
): void {
  if (typeof window === 'undefined' || !window.oaiq) return
  const payload: Record<string, unknown> = { type: 'contents' }
  if (typeof data?.amountCents === 'number') {
    payload.amount = data.amountCents
    payload.currency = (data.currency || 'usd').toUpperCase()
  }
  // event_id rides in the 4th "options" argument. It only matters once the
  // server-side Conversions API is added — OpenAI dedupes a browser event
  // against a server one when the pixel's event_id equals the API's `id`.
  // Harmless until then; sending it now means CAPI can be bolted on without
  // revisiting these call sites.
  window.oaiq('measure', event, payload, eventId ? { event_id: eventId } : undefined)
}

// --- de-duplication -------------------------------------------------------
//
// Mirrors the gtag.ts guard for the same reason: the confirmation page fires
// the purchase twice by design (once when Stripe confirms payment, once when
// the eSIM reaches `delivered`) because ~7% of delivered orders never reach
// the second trigger. Unlike Google we can't rely on a server-side
// transaction_id dedupe here, so this local guard is doing real work rather
// than just being belt-and-braces.

const SENT_KEY = 'nimvoy.oaiq.sent'
const SENT_CAP = 25

function alreadySent(txnId: string): boolean {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    return raw ? (JSON.parse(raw) as string[]).includes(txnId) : false
  } catch {
    return false // storage blocked (private mode, ITP) — accept a possible double
  }
}

function markSent(txnId: string): void {
  try {
    const raw = localStorage.getItem(SENT_KEY)
    const list = raw ? (JSON.parse(raw) as string[]) : []
    if (!list.includes(txnId)) list.push(txnId)
    localStorage.setItem(SENT_KEY, JSON.stringify(list.slice(-SENT_CAP)))
  } catch {
    /* nothing we can do */
  }
}

/** Purchase. Safe to call more than once for the same order — deduped locally
 *  on the order reference, which is also sent as the event_id. */
export function oaiqPurchase(opts: {
  transactionId: string
  valueCents: number
  currency: string
}): void {
  if (!opts.transactionId || alreadySent(opts.transactionId)) return
  markSent(opts.transactionId)
  measure(
    'order_created',
    { amountCents: opts.valueCents, currency: opts.currency },
    opts.transactionId,
  )
}

/** Customer reached the checkout page with a real plan. */
export function oaiqCheckoutStarted(priceCents?: number | null): void {
  measure('checkout_started', { amountCents: priceCents, currency: 'usd' })
}

/** Customer picked a plan — the closest thing this funnel has to add-to-cart. */
export function oaiqPlanClicked(priceCents?: number | null): void {
  measure('items_added', { amountCents: priceCents, currency: 'usd' })
}
